import { ReactNode } from "react"
import { Avatar, Box, Divider, Grid, Typography } from "@mui/material"
import { Player } from "@lottiefiles/react-lottie-player"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faUserUnlock } from "@fortawesome/pro-light-svg-icons"
import { makeStyles } from "tss-react/mui"

import { ErrorDisplay } from "../../../../../../core/api/errorTypes"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import GeneralErrorAnim from "../../../../../../shared/assets/general-error-lottie-anim.json"
import { StandardChip } from "../../../../../../shared/components/badges/StandardChip"
import { StandardButton } from "../../../../../../shared/components/buttons/StandardButton"
import { StandardSideDrawer } from "../../../../../../shared/components/drawers/Drawers"
import { LoadingSpinner } from "../../../../../../shared/components/loading/LoadingSpinner"
import { LocalizedEnum, LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { TranslationKey } from "../../../../../localization/translations"
import { useDateTime } from "../../../../../localization/useDateTime"
import { getUserDetails } from "../../../../../users/userApi"
import { User, UserLogins } from "../../../../../users/userTypes"

import { CompanyAdminUsersQuery } from "../usersApi"

type CompanyUserDetailsDrawerProps = Readonly<{
    userId: number
    open: boolean
    onClose: () => void
    onEdit: () => void
    onImpersonate?: () => void
}>

const useStyles = makeStyles()((theme) => ({
    identity: {
        display: "flex",
        flexDirection: "column",
        gap: ".5rem",
        minWidth: 0,
    },
    identityRow: {
        display: "flex",
        alignItems: "center",
        gap: ".75rem",
        minWidth: 0,
    },
    avatar: {
        width: "2.5rem",
        height: "2.5rem",
        fontSize: ".875rem",
        fontWeight: 600,
        background: theme.palette.tailwind.orange[500],
        color: theme.palette.common.white,
    },
    identityText: {
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
    },
    statusChip: {
        alignSelf: "flex-start",
    },
    body: {
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
    },
    scroll: {
        flex: 1,
        overflowY: "auto",
        display: "flex",
        flexDirection: "column",
        gap: "1.5rem",
        paddingTop: "1rem",
        paddingBottom: "1rem",
    },
    section: {
        display: "flex",
        flexDirection: "column",
        gap: ".625rem",
    },
    sectionHeader: {
        color: theme.palette.tailwind.gray[600],
    },
    row: {
        display: "flex",
        justifyContent: "space-between",
        gap: "1rem",
    },
    rowLabel: {
        color: theme.palette.tailwind.gray[500],
    },
    rowValue: {
        textAlign: "right",
        wordBreak: "break-word",
        fontSize: "1rem",
        fontWeight: 600,
        color: theme.palette.tailwind.slate[900],
    },
    chips: {
        display: "flex",
        flexWrap: "wrap",
        gap: ".5rem",
    },
    footer: {
        display: "flex",
        justifyContent: "flex-end",
        flexShrink: 0,
        // Negative horizontal margins cancel the drawer content's 1.5rem side padding so the
        // top divider spans the full drawer width while the button stays inset.
        marginLeft: "-1.5rem",
        marginRight: "-1.5rem",
        paddingTop: "1rem",
        paddingLeft: "1.5rem",
        paddingRight: "1.5rem",
        borderTop: `1px solid ${theme.palette.tailwind.slate[200]}`,
    },
    actionArea: {
        rowGap: theme.spacing(1),
        columnGap: theme.spacing(2),
    },
    actionButton: {
        flexGrow: 1,
    },
    impersonateIcon: {
        fontSize: "1rem !important",
    },
    stateBox: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flex: 1,
        minHeight: 0,
    },
    errorContent: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        gap: "1rem",
        maxWidth: "22rem",
        padding: "1.5rem",
    },
    errorImage: {
        width: "12rem",
    },
}))

function initialsFor(user: User): string {
    if (user.displayInitials) return user.displayInitials
    const parts = user.displayName.trim().split(/\s+/)
    const first = parts[0]?.charAt(0) ?? ""
    const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : ""
    return (first + last).toUpperCase()
}

type DetailRowProps = Readonly<{
    labelId: TranslationKey
    label: string
    value: ReactNode
}>

function DetailRow({ labelId, label, value }: DetailRowProps) {
    const { classes } = useStyles()

    return (
        <Box className={classes.row}>
            <LocalizedStrict id={labelId}>
                <Typography variant="body2" className={classes.rowLabel}>
                    {label}
                </Typography>
            </LocalizedStrict>
            <Typography variant="body2" className={classes.rowValue}>
                {value}
            </Typography>
        </Box>
    )
}

type UserDetailsBodyProps = Readonly<{
    user: User
    logins?: UserLogins
    onEdit: () => void
    onImpersonate?: () => void
}>

function UserDetailsBody({ user, logins, onEdit, onImpersonate }: UserDetailsBodyProps) {
    const { classes } = useStyles()
    const dateTimeFactory = useDateTime()

    const syncDetails = user.userGroup?.syncDetails
    const roles = user.roles ?? []
    const dash = "-"

    return (
        <Box className={classes.body}>
            <Box className={classes.scroll}>
                <Box className={classes.section}>
                    <LocalizedStrict id="company-user-details-section-account">
                        <Typography variant="subtitle2" className={classes.sectionHeader}>
                            Account
                        </Typography>
                    </LocalizedStrict>
                    <DetailRow labelId="company-user-details-account-user-id" label="User ID" value={user.id} />
                    <DetailRow
                        labelId="company-user-details-account-username"
                        label="Username"
                        value={user.username || dash}
                    />
                    <DetailRow
                        labelId="company-user-details-account-employee-id"
                        label="Employee ID"
                        value={user.employeeId || dash}
                    />
                    <DetailRow
                        labelId="company-user-details-account-user-group"
                        label="User group"
                        value={user.userGroup?.name ?? dash}
                    />
                    <DetailRow
                        labelId="company-user-details-account-department"
                        label="Department"
                        value={user.department?.name ?? dash}
                    />
                    <DetailRow
                        labelId="company-user-details-account-phone"
                        label="Phone"
                        value={user.phoneNumber || user.mobileNumber || dash}
                    />
                    <DetailRow
                        labelId="company-user-details-account-language"
                        label="Language"
                        value={user.language || dash}
                    />
                </Box>

                <Divider />

                <Box className={classes.section}>
                    <LocalizedStrict id="company-user-details-section-idcard">
                        <Typography variant="subtitle2" className={classes.sectionHeader}>
                            ID card
                        </Typography>
                    </LocalizedStrict>
                    <LocalizedStrict id="company-user-details-idcard-empty">
                        <Typography variant="body2" color="text.secondary">
                            No ID card connected.
                        </Typography>
                    </LocalizedStrict>
                </Box>

                <Divider />

                <Box className={classes.section}>
                    <LocalizedStrict id="company-user-details-section-signin">
                        <Typography variant="subtitle2" className={classes.sectionHeader}>
                            Sign-in
                        </Typography>
                    </LocalizedStrict>
                    <DetailRow
                        labelId="company-user-details-last-signin"
                        label="Last sign-in"
                        value={
                            logins?.lastLogin ? (
                                dateTimeFactory.formatDateTime(dateTimeFactory.fromApi(logins.lastLogin))
                            ) : (
                                <LocalizedStrict id="company-user-details-never">
                                    <span>Never</span>
                                </LocalizedStrict>
                            )
                        }
                    />
                    <DetailRow
                        labelId="company-user-details-signin-count"
                        label="Sign-in count"
                        value={logins?.loginCount ?? 0}
                    />
                </Box>

                <Divider />

                <Box className={classes.section}>
                    <LocalizedStrict id="company-user-details-section-sync">
                        <Typography variant="subtitle2" className={classes.sectionHeader}>
                            User sync
                        </Typography>
                    </LocalizedStrict>
                    {syncDetails ? (
                        <>
                            <DetailRow
                                labelId="company-user-details-sync-source"
                                label="Source"
                                value={syncDetails.source}
                            />
                            <DetailRow
                                labelId="company-user-details-sync-last-synced"
                                label="Last synced"
                                value={dateTimeFactory.formatDateTime(dateTimeFactory.fromApi(syncDetails.lastSynced))}
                            />
                        </>
                    ) : (
                        <LocalizedStrict id="company-user-details-sync-none">
                            <Typography variant="body2" color="text.secondary">
                                Not synchronized.
                            </Typography>
                        </LocalizedStrict>
                    )}
                </Box>

                <Divider />

                <Box className={classes.section}>
                    <LocalizedStrict id="company-user-details-section-roles">
                        <Typography variant="subtitle2" className={classes.sectionHeader}>
                            Roles
                        </Typography>
                    </LocalizedStrict>
                    {roles.length > 0 ? (
                        <Box className={classes.chips}>
                            {roles.map((role) => (
                                <LocalizedEnum key={role} base="company-user-details-role" enumValue={role}>
                                    <StandardChip variant="grey">{role}</StandardChip>
                                </LocalizedEnum>
                            ))}
                        </Box>
                    ) : (
                        <LocalizedStrict id="company-user-details-roles-empty">
                            <Typography variant="body2" color="text.secondary">
                                No roles assigned.
                            </Typography>
                        </LocalizedStrict>
                    )}
                </Box>
            </Box>

            <Box className={classes.footer}>
                <Grid container direction="row" justifyContent="space-evenly" className={classes.actionArea}>
                    {onImpersonate && (
                        <Grid item className={classes.actionButton}>
                            <LocalizedStrict id="company-user-details-impersonate-button">
                                <StandardButton
                                    variant="outlined"
                                    size="medium"
                                    startIcon={
                                        <FontAwesomeIcon icon={faUserUnlock} className={classes.impersonateIcon} />
                                    }
                                    onClick={onImpersonate}
                                >
                                    Impersonate
                                </StandardButton>
                            </LocalizedStrict>
                        </Grid>
                    )}
                    <Grid item className={classes.actionButton}>
                        <LocalizedStrict id="company-user-details-edit-button">
                            <StandardButton size="medium" onClick={onEdit}>
                                Edit user
                            </StandardButton>
                        </LocalizedStrict>
                    </Grid>
                </Grid>
            </Box>
        </Box>
    )
}

export function CompanyUserDetailsDrawer({
    userId,
    open,
    onClose,
    onEdit,
    onImpersonate,
}: CompanyUserDetailsDrawerProps) {
    const { classes } = useStyles()

    const detailsResult = useApiQuery(getUserDetails, {
        queryName: CompanyAdminUsersQuery.USER_DETAILS,
        dependencies: { userId },
        enabled: open,
        errorDisplay: ErrorDisplay.NONE,
    })

    const user = detailsResult.data?.user
    const logins = detailsResult.data?.logins

    const headerIdentity: ReactNode = user ? (
        <Box className={classes.identity}>
            <Box className={classes.identityRow}>
                <Avatar className={classes.avatar}>{initialsFor(user)}</Avatar>
                <Box className={classes.identityText}>
                    <Typography variant="subtitle1" component="span" noWrap>
                        {user.displayName}
                    </Typography>
                    <Typography variant="body2" component="span" noWrap color="text.secondary">
                        {user.email}
                    </Typography>
                </Box>
            </Box>
            <LocalizedStrict id={user.isActive ? "company-users-status-active" : "company-users-status-inactive"}>
                <StandardChip variant={user.isActive ? "success" : "grey"} className={classes.statusChip}>
                    {user.isActive ? "Active" : "Inactive"}
                </StandardChip>
            </LocalizedStrict>
        </Box>
    ) : (
        ""
    )

    return (
        <StandardSideDrawer
            name="company-user-details"
            title={headerIdentity}
            open={open}
            onClose={onClose}
            width="32rem"
            horizontalPadding="1.5rem"
        >
            {detailsResult.isError ? (
                <Box className={classes.stateBox}>
                    <Box className={classes.errorContent}>
                        <Player autoplay loop controls={false} src={GeneralErrorAnim} className={classes.errorImage} />
                        <LocalizedStrict id="company-user-details-error-title">
                            <Typography variant="subtitle1">Couldn't load user</Typography>
                        </LocalizedStrict>
                        <LocalizedStrict id="company-user-details-error-message">
                            <Typography variant="body2" color="text.secondary">
                                Something went wrong while loading this user. Please try again.
                            </Typography>
                        </LocalizedStrict>
                    </Box>
                </Box>
            ) : user ? (
                <UserDetailsBody user={user} logins={logins} onEdit={onEdit} onImpersonate={onImpersonate} />
            ) : (
                <Box className={classes.stateBox}>
                    <LoadingSpinner />
                </Box>
            )}
        </StandardSideDrawer>
    )
}
